// The side panel — the one animation in this product that already worked, corrected.
//
// It is the in-repo model for everything else: a CSS transition driven by Base UI's
// `data-starting-style` / `data-ending-style`, with no library and no keyframe. Three
// things were wrong with it and none of them were the approach.
//
// **The scrim ran at a different speed from the panel.** 150ms under a 200ms panel, so
// the page finished dimming before anything arrived to read. There is no scrim duration
// token on purpose — a separate name is a licence to drift — and both now take the
// panel's.
//
// **The transition was a bare `transition` shorthand**, which resolves to twenty-two
// properties including `box-shadow` and `outline`. A focus ring that fades in arrives
// after the eye has already looked, and every one of those properties is a chance to
// animate something that triggers layout. Narrowed to opacity and translate, the two
// that run on the compositor.
//
// **The travel distance was a literal.** `2.5rem` written in eight places, once per
// side per direction, with no way to neutralise it under `prefers-reduced-motion`. It
// is `--motion-travel-panel` now, which the generated stylesheet re-points to `0rem`
// under reduce — so the panel cross-fades in place, over the same duration, without a
// single component knowing the setting exists.
//
// Composed from the primitive rather than wrapping `SheetContent`, because that
// component renders its own overlay internally and offers no way to reach it.
//
// # A SHEET WITH A SHEET ON TOP OF IT
//
// A create flow opened from inside a pane is a pane over a pane, and the product owner
// wants them to read as stacked paper — the one underneath offset slightly left so its
// edge shows, like a ledger. That is a fact about a SHEET rather than about a pane, and
// this file is the only place with the popup in its hands, so it lives here.
//
// Base UI already answers most of it, which is why nothing here re-implements a stack.
// Read out of its source rather than hoped for:
//
//   - `useDialogRoot` passes `escapeKey: isTopmost` to `useDismiss`, so a sheet with a
//     nested one open has Escape DISABLED rather than merely out-ordered — and its
//     `outsidePress` returns false unconditionally unless `isTopmost`. Escape and the
//     scrim both dismiss the top and nothing else, for free.
//   - `DialogBackdrop` runs `enabled: forceRender || !nested`, so a NESTED SHEET DRAWS
//     NO BACKDROP. One scrim for the stack is the primitive's own default. Two stacked
//     full-viewport `backdrop-filter`s is the 16.7ms→30.4ms cost in the motion doc paid
//     twice, for a page nobody can read anyway.
//   - nesting is detected from the REACT tree, so a sheet rendered inside another
//     sheet's subtree is nested even though it portals to `<body>`.
//
// Two things are riksu's, and both are here.
//
// `inert` ON THE ONE UNDERNEATH. Base UI marks everything outside the active floating
// tree `aria-hidden` and traps Tab, and neither is enough here: the underneath sheet is
// deliberately VISIBLE — that is the whole point of the ledger — and a visible,
// non-inert form under a modal child is exactly "Tab walks into a form nobody can see",
// with a pointer twin. `inert` also blocks the pointer and programmatic focus, which
// `aria-hidden` does not, and it is one attribute rather than a second focus mechanism
// beside the primitive's. It is set from Base UI's own `nestedDialogOpen`, which flips
// on `open` rather than at the end of the exit transition — so the parent is reachable
// again by the time focus is restored to it.
//
// THE OFFSET, from `--spacing-underlap`. Not a `motion.travel.*` token, and the
// reasoning is in that token's own comment: those are re-pointed to `0rem` under
// `prefers-reduced-motion`, and a resting offset re-pointed to zero does not reduce
// motion, it deletes the only thing saying a sheet is underneath. The motion is the
// TRANSITION between resting positions, which is `panelMotion`'s existing duration and
// curve on the same `translate` property — already re-pointed, no component branching.
//
// No scale. It would say what the offset already says, soften the text under it at a
// non-integer factor, and need a `transform-origin` decision it has no basis for. Paper
// does not shrink when you put paper on it.

import { Dialog as SheetPrimitive } from "@base-ui/react/dialog";
import { XIcon } from "lucide-react";
import { type HTMLMotionProps, motion } from "motion/react";
import { createContext, type RefObject, useContext, useRef } from "react";
import { Button } from "@/components/ui/button";
import {
  SheetClose,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  Sheet as SheetRoot,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { panelUnderlap } from "./motion";
import { durationOf, easeOf, travelOf } from "./motion-runtime";

export { SheetClose, SheetDescription, SheetFooter, SheetHeader, SheetTitle, SheetTrigger };

/**
 * WHO DECIDES WHEN THE PANEL LEAVES THE DOM — and it is no longer Base UI.
 *
 * # The race this closes, measured rather than reasoned about
 *
 * Base UI holds a closing popup until `element.getAnimations()` resolves, which is why a
 * CSS transition needs no handshake: the transition is registered by the browser as soon
 * as the ending styles are computed, so it is in that list when Base UI looks.
 *
 * `motion` starts its animations on its OWN frame loop. Measured in Chromium on
 * 2026-08-17, sampling the panel every 20ms: the entrance is a real WAAPI animation
 * (`Animation`, `running`, keyframes carrying `opacity`, transform `40px → 0`), and on the
 * way out **the element was already gone at the first sample** — no `data-ending-style`
 * ever observed, `0` eased frames painted, at 1x and at 4x alike. Base UI looked, found an
 * empty list because motion's frame had not run yet, and unmounted. **The entrance proves
 * the interop and the exit disproves it**, which is why one measurement of the opening
 * would have certified this as working.
 *
 * That is exactly the defect this repository has already paid for once, in
 * `moduleNavMotion`: a fade that was real, eased and token-driven, every frame of which
 * ran somewhere nobody could see. A class name says an element is ALLOWED to animate; only
 * a browser says where it was while it did.
 *
 * # The handshake, which is the primitive's own and not a workaround
 *
 * `preventUnmountOnClose()` on the close event tells Base UI to keep the DOM, and
 * `actionsRef.current.unmount()` is what the documentation calls for *"after any externally
 * controlled closing animation finishes"*. So the question moves from *has anything
 * registered an animation yet* — which is a race — to *has the exit finished*, which motion
 * answers exactly.
 *
 * It lives on a context rather than as a prop because the two ends are two components: the
 * ROOT is where the close is intercepted and the POPUP is where the animation completes,
 * and every caller in this product composes them through `Pane` without knowing either
 * exists.
 */
const SheetUnmount = createContext<RefObject<SheetPrimitive.Root.Actions | null> | null>(null);

/**
 * The dialog root, holding the DOM open for the exit.
 *
 * The wrapper is here rather than in `src/components/ui/sheet.tsx` because that file is
 * overwritten by `shadcn add`; a rule about generated code, and the reason `SheetContent`
 * is composed from the primitive in this file too.
 */
export function Sheet({ onOpenChange, ...props }: SheetPrimitive.Root.Props) {
  const actions = useRef<SheetPrimitive.Root.Actions | null>(null);
  return (
    <SheetUnmount.Provider value={actions}>
      <SheetRoot
        {...props}
        actionsRef={actions}
        onOpenChange={(open, details) => {
          // ONLY ON THE WAY OUT. Asking for this on an opening event would hold a dialog
          // that has nothing to unmount, and the entrance needs no handshake at all —
          // nothing is waiting on it.
          if (!open) {
            details.preventUnmountOnClose();
          }
          onOpenChange?.(open, details);
        }}
      />
    </SheetUnmount.Provider>
  );
}

/** Where the panel sits, and therefore which way it travels. */
type Side = "top" | "right" | "bottom" | "left";

/**
 * Where the panel SITS, per side. The travel is no longer here — see `awayFrom`.
 *
 * These four are pure layout and carry no motion at all, which is the split the move to
 * `motion` bought: a side used to decide both where the panel rests and which way it
 * arrives, in one string, so the two could not be read apart.
 */
const SEAT: Record<Side, string> = {
  right: "inset-y-0 right-0 h-full w-3/4 border-l sm:max-w-sm",
  left: "inset-y-0 left-0 h-full w-3/4 border-r sm:max-w-sm",
  bottom: "inset-x-0 bottom-0 h-auto border-t",
  top: "inset-x-0 top-0 h-auto border-b",
};

/** Resting: on its seat, fully opaque. One object, because nothing about it varies. */
const SEATED = { opacity: 1, x: 0, y: 0 } as const;

/**
 * WHERE THE PANEL IS BEFORE IT ARRIVES AND AFTER IT HAS GONE, per side.
 *
 * The sign differs — a panel on the right arrives from `+x` and one on the left from `-x`
 * — and the MAGNITUDE is one token for all four, so `prefers-reduced-motion` neutralises
 * every side at once by re-pointing `--motion-travel-panel` to `0rem`. That is the same
 * property the class-driven version had and it is the reason the distance is read from the
 * token at animation time rather than written here as a number: a literal would be a
 * distance no reader could turn off.
 *
 * `--motion-travel-panel` is 2.5rem and NEVER the panel's own width. The token file
 * carries that rule — a full-width slide is the area-scaled movement that is an actual
 * vestibular trigger — and a percentage cannot be re-pointed to zero, so the one reader
 * who asked for less movement would be handed the most.
 */
function awayFrom(side: Side) {
  const travel = travelOf("--motion-travel-panel");
  switch (side) {
    case "right":
      return { opacity: 0, x: travel, y: 0 };
    case "left":
      return { opacity: 0, x: -travel, y: 0 };
    case "bottom":
      return { opacity: 0, x: 0, y: travel };
    case "top":
      return { opacity: 0, x: 0, y: -travel };
  }
}

/**
 * The timing for a phase, taken from the tokens on every animation.
 *
 * Enter and exit are different DURATIONS and the same CURVE — `--motion-ease-exit` was an
 * `ease.in` until 2026-08-09, flat for its first fifth, so every exit in the product began
 * by standing still; it points at `ease.out` now and seven declarations took the
 * correction from that one line.
 */
function timingFor(leaving: boolean) {
  return {
    duration:
      durationOf(leaving ? "--motion-duration-panel-exit" : "--motion-duration-panel-enter") / 1000,
    ease: easeOf("--motion-ease-exit", [0, 0, 0.2, 1]),
  };
}

/**
 * WHERE A SHEET SITS while another one is open on top of it, per side.
 *
 * Away from its own edge, so the sheet above lands flush and the one underneath shows
 * a strip of itself — the ledger. Written per side for the same reason `TRAVEL` is:
 * the sign differs, and the magnitude is one token so retuning it moves all four.
 *
 * `data-nested-dialog-open` is Base UI's, set on a popup that HAS a nested one open. It
 * is deliberately not `--nested-dialogs` (the count): the stack stops at two, so a
 * multiplier would be arithmetic that can only ever produce one answer.
 */
const UNDERLAP: Record<Side, string> = {
  right: "data-nested-dialog-open:-translate-x-(--spacing-underlap)",
  left: "data-nested-dialog-open:translate-x-(--spacing-underlap)",
  bottom: "data-nested-dialog-open:-translate-y-(--spacing-underlap)",
  top: "data-nested-dialog-open:translate-y-(--spacing-underlap)",
};

export type SheetContentProps = SheetPrimitive.Popup.Props & {
  readonly side?: Side;
  readonly showCloseButton?: boolean;
  readonly showBackdrop?: boolean;
};

export function SheetContent({
  className,
  children,
  side = "right",
  showCloseButton = true,
  showBackdrop = true,
  ...props
}: SheetContentProps) {
  const unmount = useContext(SheetUnmount);
  return (
    <SheetPrimitive.Portal data-slot="sheet-portal">
      {showBackdrop ? (
        <SheetPrimitive.Backdrop
          data-slot="sheet-overlay"
          className="fixed inset-0 z-(--layer-modal) bg-surface-scrim supports-backdrop-filter:backdrop-blur-xs"
          // THE SCRIM TAKES THE PANEL'S TIMING and has none of its own. There is no scrim
          // duration token on purpose — a separate name is a licence to drift, and a drift
          // shows as a page that finishes dimming before anything arrives to read.
          render={(backdrop, state) => (
            <motion.div
              {...(backdrop as HTMLMotionProps<"div">)}
              initial={{ opacity: 0 }}
              animate={{ opacity: state.transitionStatus === "ending" ? 0 : 1 }}
              transition={timingFor(state.transitionStatus === "ending")}
            />
          )}
        />
      ) : null}
      <SheetPrimitive.Popup
        data-slot="sheet-content"
        data-side={side}
        // `aria-modal` and the focus target are not decoration here: without them the
        // reader lands on whatever element happens to be first inside the panel — on
        // the inbox that was an unlabelled textarea — and hears nothing that says a
        // dialog opened or what it is called.
        aria-modal={showBackdrop ? "true" : undefined}
        className={cn(
          "fixed z-(--layer-modal) flex flex-col gap-4 bg-popover bg-clip-padding text-shell-body text-popover-foreground shadow-(--shadow-overlay)",
          panelUnderlap,
          SEAT[side],
          UNDERLAP[side],
          className,
        )}
        // TWO THINGS BASE UI DOES NOT DO, and one it does better than a class could.
        //
        // `inert` ON THE ONE UNDERNEATH. See the note at the top: `aria-hidden` and a Tab
        // trap are not enough for a sheet that stays visible under another one.
        //
        // THE ENTRANCE AND THE EXIT, driven by `motion` rather than by a transition. The
        // phase comes from Base UI's own `transitionStatus`, so there is no second source
        // of truth about whether the panel is arriving or leaving — `"ending"` is set by
        // the same machinery that decides when to unmount.
        //
        // AND BASE UI WAITS FOR IT, WITHOUT BEING ASKED. `useAnimationsFinished` awaits
        // `element.getAnimations()` and RE-CHECKS when an animation is replaced mid-flight,
        // and `motion` drives these through the Web Animations API — so the panel is in
        // that list and cannot be unmounted mid-exit. That is why no `actionsRef` /
        // `unmount()` handshake appears here: the interop is real rather than assumed, and
        // it is measured by `tests/e2e/pane-motion.mjs`, which reads the panel's own
        // geometry per painted frame precisely so "did not animate" and "had not started"
        // can be told apart.
        //
        // `popup` carries the class, the children, the ref and every handler Base UI merged
        // in, so spreading it is not optional. The cast is `motion`'s drag/animation
        // handlers colliding by NAME with React's DOM ones; it widens nothing at runtime.
        render={(popup, state) => (
          <motion.div
            {...(popup as HTMLMotionProps<"div">)}
            inert={state.nestedDialogOpen}
            initial={awayFrom(side)}
            animate={state.transitionStatus === "ending" ? awayFrom(side) : SEATED}
            transition={timingFor(state.transitionStatus === "ending")}
            // THE PANEL SAYS WHEN IT HAS GONE. See `SheetUnmount`: the root has told Base
            // UI to keep the DOM, so this call is the only thing that ever removes it, and
            // the guard is what keeps the ENTRANCE from doing so — `onAnimationComplete`
            // fires on both, and unmounting at the end of an arrival would delete the pane
            // the instant it finished opening.
            onAnimationComplete={() => {
              if (state.transitionStatus === "ending") {
                unmount?.current?.unmount();
              }
            }}
          />
        )}
        {...props}
      >
        {children}
        {showCloseButton ? (
          <SheetPrimitive.Close
            data-slot="sheet-close"
            render={<Button variant="ghost" className="absolute top-3 right-3" size="icon-sm" />}
          >
            <XIcon />
            <span className="sr-only">Close</span>
          </SheetPrimitive.Close>
        ) : null}
      </SheetPrimitive.Popup>
    </SheetPrimitive.Portal>
  );
}
